import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { AppState } from '../../app.state';
import { LogService } from '../../services/log.service';
import { AuthService } from '@services/auth.service';
import { SecurityPolicies } from '@services/auth-variables';
import { take } from 'rxjs';
import { deviceTypeCategory } from '@interfaces/deviceTypeCategory';
import { TankService } from '@services/tank.service';

interface TankSearchResult {
  uniqueId: number;
  tankId: number;
  isSkyTankAssociated?: boolean;
  canUnassignFromGlobalSearch?: boolean;
  isAffiliatedTank: number;
  tankName: string;
  organizationName: string;
  locationName: string;
  locationId: number;
  affiliatedLocationId?: number;
  rtunumber: number;
  deviceTankNumber?: number;
  localizedInvTime?: string;
  gatewayRTUNumber?: number;
  invTime?: string;
  min0: number;
  deviceTypeCategoryId: number;
  prodDesc: string;
  active: boolean;
  isReplica: boolean;
  locationTankCount?: number; // Tank Count for the location
}

/**
 * Component that displays the results of a global tank search in a paginated table
 */
@Component({
  selector: 'global-tank-search-results',
  templateUrl: './global-tank-search-results.component.html',
  styleUrls: ['./global-tank-search-results.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GlobalTankSearchResultComponent implements OnInit, AfterViewInit {
  @ViewChild(MatPaginator) private paginator: MatPaginator;
  public dataSource: MatTableDataSource<TankSearchResult> = new MatTableDataSource<TankSearchResult>();
  public baseExternalURL: string;

  private readonly baseColumnDefs: string[] = [
    'affiliated',
    'name',
    'organization',
    'location',
    'locationTankCount',
    'tanknumber',
    'deviceTankNumber',
    'product',
    'minnumber',
    'active',
    'replica',
  ];

  public columnDefs: string[] = [];

  public userCanViewLocation: boolean = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public tankSearchResult: TankSearchResult[],
    public dialogRef: MatDialogRef<GlobalTankSearchResultComponent>,
    private logger: LogService,
    private translate: TranslateService,
    private store: Store<AppState>,
    private authService: AuthService,
    private tankService: TankService,
    private cdr: ChangeDetectorRef
  ) {
      this.authService.passesPolicyOf(SecurityPolicies.RequiresManageLocations)
      .pipe(take(1))
      .subscribe((hasAccess) => {
        this.userCanViewLocation = hasAccess;
        this.cdr.markForCheck();
      });
  }

  public ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
  }

  public ngOnInit(): void {
    this.store.select('userInformation').pipe(take(1)).subscribe((userInfo) => {
      if (userInfo) {
        this.baseExternalURL = userInfo.legacyAppLoginURL;
        this.dataSource = new MatTableDataSource<TankSearchResult>(this.tankSearchResult);
        this.columnDefs = this.getColumnDefs(this.tankSearchResult);
      }
    });
  }

  public closeDialog(): void {
    this.dialogRef.close();
  }

  public get canViewLocation(): boolean {
    return this.userCanViewLocation;
  }

  public get isSkyTankContext(): boolean {
    return this.hasSkyTankAssociatedRows(this.dataSource?.data ?? []);
  }

  public onUnassignComplete(tankId: number): void {
    const requestRows = (this.dataSource.data ?? []).filter((row) => row.tankId === tankId);
    if (requestRows.length === 0) {
      return;
    }

    requestRows.forEach((requestRow) => {
      const requestUniqueId = requestRow.uniqueId;
      this.tankService
        .getGlobalSearchRowByUniqueId(requestUniqueId)
        .pipe(take(1))
        .subscribe({
          next: (latestRow) => {
            this.applyRowRefresh(requestUniqueId, latestRow);
          },
          error: () => {
            this.applyRowRefresh(requestUniqueId, null);
          },
        });
    });
  }

  private applyRowRefresh(uniqueId: number, latestRow: Partial<TankSearchResult> | null): void {
    const current = this.dataSource.data ?? [];
    const index = current.findIndex((row) => row.uniqueId === uniqueId);
    if (index === -1) {
      return;
    }

    const currentRow = current[index];
    const nextRows = [...current];
    nextRows[index] = latestRow
      ? {
          ...currentRow,
          ...latestRow,
        }
      : {
          ...currentRow,
          canUnassignFromGlobalSearch: false,
        };
    this.dataSource.data = nextRows;
    this.columnDefs = this.getColumnDefs(this.dataSource.data);
    this.cdr.markForCheck();
  }

  public canShowAction(row: TankSearchResult): boolean {
    return row.canUnassignFromGlobalSearch === true;
  }

  public isSkyTankAssociatedRow(row: TankSearchResult): boolean {
    if (row.isSkyTankAssociated !== undefined) {
      return row.isSkyTankAssociated;
    }

    if (row.deviceTypeCategoryId === deviceTypeCategory.skyTankGateway) {
      return true;
    }
    if (row.deviceTypeCategoryId === deviceTypeCategory.skyTankSensor) {
      return true;
    }
    return !!row.gatewayRTUNumber;
  }

  private hasSkyTankAssociatedRows(rows: TankSearchResult[]): boolean {
    return rows.some((row) => this.isSkyTankAssociatedRow(row));
  }

  private getColumnDefs(rows: TankSearchResult[]): string[] {
    const columns = [...this.baseColumnDefs];
    if (this.hasSkyTankAssociatedRows(rows)) {
      columns.push('skytankGatewayRtu');
    }
    columns.push('lastInventoryTime');
    if (rows.some((row) => this.canShowAction(row))) {
      columns.push('actions');
    }
    return columns;
  }

}
